import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { getProducts } from '../../lib/api';

const DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1541643600914-78b084683601?w=100';
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const SUGGESTION_LIMIT = 8;

/**
 * @param {'desktop' | 'mobile-inline' | 'mobile-panel'} variant
 * @param {() => void} [onNavigate] - called after selecting a product (e.g. close mobile menu)
 */
export function HeaderProductSearch({ variant = 'desktop', onNavigate, autoFocus = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const navigate = useNavigate();

  const isDark = variant === 'desktop' || variant === 'mobile-inline';

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      const reqId = ++requestIdRef.current;
      try {
        const res = await getProducts({
          search: trimmed,
          limit: SUGGESTION_LIMIT,
        });
        if (reqId !== requestIdRef.current) return;
        setResults(Array.isArray(res.data) ? res.data : []);
        setOpen(true);
        setActiveIndex(-1);
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        console.error('Product search failed:', err);
        setResults([]);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const goToProduct = useCallback(
    (product) => {
      if (!product?.slug) return;
      setQuery('');
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      onNavigate?.();
      navigate(`/product/${product.slug}`);
    },
    [navigate, onNavigate],
  );

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter') && results.length) {
      setOpen(true);
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
      return;
    }
    if (!results.length) {
      if (e.key === 'Enter' && query.trim().length >= MIN_QUERY_LENGTH) {
        e.preventDefault();
        onNavigate?.();
        navigate(`/all-products?q=${encodeURIComponent(query.trim())}`);
        setQuery('');
        setOpen(false);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        goToProduct(results[activeIndex]);
      } else {
        goToProduct(results[0]);
      }
    }
  };

  const showDropdown =
    open &&
    query.trim().length >= MIN_QUERY_LENGTH &&
    (loading || results.length > 0 || !loading);

  const inputClasses = isDark
    ? 'w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-[#D4AF37] focus:bg-white/15 pl-9 pr-8 py-2 text-sm rounded-sm'
    : 'w-full bg-[#F7F6F3] border border-gray-200 text-[#0B1F3B] placeholder:text-gray-400 focus:outline-none focus:border-[#D4AF37] pl-9 pr-8 py-2.5 text-sm rounded-sm';

  return (
    <div
      ref={containerRef}
      className={`relative ${variant === 'desktop' ? 'w-56 xl:w-72' : 'w-full'}`}
      data-testid={`header-product-search-${variant}`}
    >
      <div className="relative">
        <Search
          className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${
            isDark ? 'text-white/50' : 'text-gray-400'
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (query.trim().length >= MIN_QUERY_LENGTH) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search products..."
          autoComplete="off"
          enterKeyHint="search"
          className={inputClasses}
          aria-label="Search products"
          aria-expanded={showDropdown}
          aria-controls="header-search-suggestions"
          data-testid="header-search-input"
        />
        {query && !loading && (
          <button
            type="button"
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 ${
              isDark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={() => {
              setQuery('');
              setResults([]);
              setOpen(false);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            data-testid="header-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {loading && (
          <Loader2
            className={`absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin ${
              isDark ? 'text-white/50' : 'text-gray-400'
            }`}
          />
        )}
      </div>

      {showDropdown && (
        <div
          id="header-search-suggestions"
          role="listbox"
          className={`absolute left-0 right-0 mt-1 bg-white shadow-2xl border border-gray-100 max-h-[70vh] overflow-y-auto z-[10001] ${
            variant === 'mobile-panel' ? 'rounded-sm' : ''
          }`}
          data-testid="header-search-suggestions"
        >
          {loading && results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">No products found</div>
          ) : (
            <ul>
              {results.map((product, index) => (
                <li key={product.id || product.slug} role="option" aria-selected={index === activeIndex}>
                  <Link
                    to={`/product/${product.slug}`}
                    className={`flex items-center gap-3 px-3 py-2.5 hover:bg-[#F7F6F3] transition-colors ${
                      index === activeIndex ? 'bg-[#F7F6F3]' : ''
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      goToProduct(product);
                    }}
                    data-testid={`header-search-result-${product.slug}`}
                  >
                    <img
                      src={product.images?.[0] || DEFAULT_IMAGE}
                      alt=""
                      className="w-10 h-10 object-cover bg-[#F7F6F3] flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#0B1F3B] truncate">{product.name}</p>
                      {(product.volume_ml || product.product_type) && (
                        <p className="text-xs text-gray-500 truncate">
                          {[product.product_type, product.volume_ml].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default HeaderProductSearch;
